import { MouseEvent, useRef } from "react";

import { useTranslation } from "next-i18next";

import { Heart as HeartIcon } from "@/assets/icons/24/Heart";
import { IconStateHeartFilled } from "@/assets/icons/figma/IconStateHeartFilled";
import { IconStateHeartOutline } from "@/assets/icons/figma/IconStateHeartOutline";
import { useGuestModalGuard } from "@/common/hooks/guest.hooks";
import { useProWorkspace } from "@/features/pro-buyer-workspaces/components/workspace-context";
import { useProductCard } from "@/features/search/components/product-card/product-card.context";
import { useToggleFavorite } from "@/features/search/hooks/useToggleFavorite";
import { Heading } from "@/ui/cva/Heading";

/**
 * The heart. Positioned absolutely, so render it inside the image's relative container
 * (the default card layout already does).
 *
 * `favoriteId` comes back per result for whoever holds the bearer token, so an anonymous
 * visitor always sees an empty heart — clicking it opens the signup wall rather than
 * silently doing nothing.
 */
export const ProductCardFavorite = () => {
  const { t } = useTranslation();
  const { product } = useProductCard();
  const { guestGuard } = useGuestModalGuard();
  const { toggle, isToggling, isFavoriting } = useToggleFavorite(product);
  /* WHO-6629: Pro Buyers inside the workspace gate get the project menu; everyone else, a no-op. */
  const workspace = useProWorkspace();
  const buttonRef = useRef<HTMLButtonElement>(null);

  /* Filled while a create is in flight: the id it fills with only exists once the gateway answers. */
  const isFavorite = !!product.favoriteId || isFavoriting;

  const handleClick = async (event: MouseEvent<HTMLButtonElement>) => {
    /* The whole card is a link — a heart click must not navigate to the PDP. */
    event.preventDefault();
    event.stopPropagation();

    const pass = await guestGuard({
      title: (
        <span className="flex flex-col space-y-2">
          <HeartIcon className="mx-auto h-8 w-8 text-attention-500" />
          <Heading as="h4" className="text-center">
            {t("signup-login-wall:signup-login-wall-favorite-title")}
          </Heading>
        </span>
      ),
      description: t("signup-login-wall:signup-login-wall-favorite-desc"),
      referrer: "favorites",
    });

    if (!pass) {
      return;
    }

    /* R2: a piece that sits in projects asks before the heart empties. */
    if (isFavorite && !(await workspace.confirmUnfavorite(product.id))) {
      return;
    }

    const outcome = await toggle();

    if (outcome === "added") {
      workspace.afterFavoriteSaved(product.id, buttonRef.current);
    }
  };

  return (
    <button
      ref={buttonRef}
      type="button"
      aria-label={
        isFavorite
          ? t("common:remove-from-favorites")
          : t("common:add-to-favorites")
      }
      aria-pressed={isFavorite}
      /* One toggle at a time — a second click would fire a second mutation for the same product. */
      disabled={isToggling}
      className="product-card-favorite"
      /* WHO-6629 F25: lets the project menu find this heart again after the list re-renders. */
      data-favorite-heart={product.id}
      onClick={handleClick}
    >
      {isFavorite ? (
        <IconStateHeartFilled className="size-6 shrink-0 text-attention-500 md:size-8" />
      ) : (
        /*
         * Empty state gets the filled heart in white underneath as its own backdrop — same shape and
         * viewBox, so the outline stays legible over a pale photo without a square behind it.
         */
        <span className="product-card-heart">
          <IconStateHeartFilled className="absolute inset-0 size-full text-white" />
          <IconStateHeartOutline className="absolute inset-0 size-full text-attention-600" />
        </span>
      )}
    </button>
  );
};
